import { useState, useCallback } from 'react';
import { IInstitution } from '../types/interfaces/IInstitution';
import * as institutionService from '../services/institutionService';
import { getApiErrorMessage } from '../services/core/apiResponseHelpers';

export function useInstitutions() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const getAllInstitutions = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await institutionService.getInstitutions();

      if (response.success && response.data) {
        return response.data; // Retornar data directa
      } else {
        setError(getApiErrorMessage(response, 'Error al cargar instituciones'));
        return null;
      }
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Error desconocido';
      setError(errorMessage);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const getInstitutionById = useCallback(async (id: string) => {
    setLoading(true);
    setError(null);

    try {
      const response = await institutionService.getInstitutionById(id);

      if (response.success && response.data) {
        return response.data; // Retornar data directa
      } else {
        setError(getApiErrorMessage(response, 'Error al cargar institución'));
        return null;
      }
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Error desconocido';
      setError(errorMessage);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const createInstitution = useCallback(
    async (
      institutionData: Omit<IInstitution, 'id' | 'createdAt' | 'updatedAt'>
    ) => {
      setLoading(true);
      setError(null);

      try {
        const response =
          await institutionService.createInstitution(institutionData);

        if (response.success && response.data) {
          return response.data; // Retornar data directa
        } else {
          setError(getApiErrorMessage(response, 'Error al crear institución'));
          return null;
        }
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : 'Error desconocido';
        setError(errorMessage);
        return null;
      } finally {
        setLoading(false);
      }
    },
    []
  );

  const updateInstitution = useCallback(
    async (id: string, institutionData: Partial<IInstitution>) => {
      setLoading(true);
      setError(null);

      try {
        const response = await institutionService.updateInstitution(
          id,
          institutionData
        );

        if (response.success && response.data) {
          return response.data; // Retornar data directa
        } else {
          setError(getApiErrorMessage(response, 'Error al actualizar institución'));
          return null;
        }
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : 'Error desconocido';
        setError(errorMessage);
        return null;
      } finally {
        setLoading(false);
      }
    },
    []
  );

  const deleteInstitution = useCallback(async (id: string) => {
    setLoading(true);
    setError(null);

    try {
      const response = await institutionService.deleteInstitution(id);

      if (response.success) {
        return true; // Retornar éxito
      } else {
        setError(getApiErrorMessage(response, 'Error al eliminar institución'));
        return false;
      }
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Error desconocido';
      setError(errorMessage);
      return false;
    } finally {
      setLoading(false);
    }
  }, []);

  const toggleInstitutionStatus = useCallback(async (id: string) => {
    setLoading(true);
    setError(null);

    try {
      const response = await institutionService.toggleInstitutionStatus(id);

      if (response.success && response.data) {
        return response.data; // Retornar data directa
      } else {
        setError(
          getApiErrorMessage(response, 'Error al cambiar estado de la institución')
        );
        return null;
      }
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Error desconocido';
      setError(errorMessage);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    getAllInstitutions, // Retorna IInstitution[] o null
    getInstitutionById, // Retorna IInstitution o null
    createInstitution, // Retorna IInstitution o null
    updateInstitution, // Retorna IInstitution o null
    deleteInstitution, // Retorna boolean
    toggleInstitutionStatus, // Retorna IInstitution o null
    loading,
    error,
  };
}
